import * as React from 'react'
import { MultipleDatepicker } from '$views/components/MultipleDatepicker'
import { Text, Select, Block, NativeSelect, Division } from '$views/components'
import { formatDateToDayHourYear } from '$utils/dateFormatter'
import { MediaQueryOwn } from '$views/components/MediaQueryOwn'
import { Icon } from '$views/components/Icon'
import { theme } from '$utils/theme'
import crossIcon from '$icons/cross.svg'
import { Link } from '$views/components/Link'
import { draft } from '$utils/services/formDraftService'
import { ErrorText } from '$views/components/Input'
import { DatepickerModel, getHoursAndMinutes } from '$views/LongTermRecommendation/CheckoutForm/CheckoutForm.model'
import { validateDay, validateAllDays } from '$views/LongTermRecommendation/CheckoutForm/CheckoutForm.validator'

interface Props {
  draftName: string
  draftPropertyName: string
  activeValidator?: boolean
}

export const DatepickerField: React.FC<Props> = ({ draftName, draftPropertyName, activeValidator }) => {
  const [days, setDays] = React.useState<DatepickerModel[]>([])

  const saveFields = updatedDays => {
    setDays(updatedDays)
    draft.set(draftName, { [draftPropertyName]: updatedDays })
  }

  React.useEffect(() => {
    const draftDays = draft.get(draftName)
    if (draftDays && draftDays[draftPropertyName]) {
      setDays(draftDays[draftPropertyName])
    }
  }, [])

  const addDay = (newDay: Date) => {
    const updatedDays = [...days, { date: newDay, hours: [{ start: undefined, duration: undefined }] }]
    saveFields(updatedDays)
  }

  const removeDay = (deletedDay: Date) => {
    const daysAfterRemove = days.filter(day => day.date.getTime() !== deletedDay.getTime())
    saveFields(daysAfterRemove)
  }

  const updateDays = (updatedDay: Date) => {
    if (days.filter(day => day.date.getTime() === updatedDay.getTime()).length > 0) {
      removeDay(updatedDay)
    } else {
      addDay(updatedDay)
    }
  }

  const addHour = indexDay => {
    const updatedDays = days.map((day, index) => {
      if (indexDay === index) day.hours.push({ start: undefined, end: undefined })
      return day
    })
    saveFields(updatedDays)
  }

  const removeHour = (indexDay, indexHour) => {
    if (indexHour > 0) {
      const updatedDays = days.map((day, index) => {
        if (indexDay === index) days[indexDay].hours.splice(indexHour, 1)
        return day
      })
      saveFields(updatedDays)
    } else {
      removeDay(days[indexDay].date)
    }
  }

  const handleStartHour = (event, indexDay, indexHour) => {
    const updatedDays = days.map((day, index) => {
      if (index === indexDay) {
        day.hours[indexHour].start = event.value
      }
      return day
    })
    saveFields(updatedDays)
  }

  const handleEndHour = (event, indexDay, indexHour) => {
    const updatedDays = days.map((day, index) => {
      if (index === indexDay) {
        day.hours[indexHour].end = event.value
      }
      return day
    })
    saveFields(updatedDays)
  }

  const getDays = selectedDays => selectedDays.map(day => day.date)

  return (
    <>
      <MultipleDatepicker days={getDays(days)} updateDays={updateDays} />
      {days.map((day, indexDay) => (
        <div key={`day-${indexDay}`}>
          <Text weight="medium" menu marginBottom="base" marginTop="small">
            {formatDateToDayHourYear(day.date)}
          </Text>
          {day.hours.map((hour, indexHour) => (
            <div key={`hour-${indexHour}`}>
              <Block>
                <Block flex alignItems="center" marginTop="mini">
                  <MediaQueryOwn minWidth={theme.breakpoints.desktop}>
                    <Select
                      options={getHoursAndMinutes()}
                      inline
                      placeholder={'Hora inicio'}
                      onChange={event => handleStartHour(event, indexDay, indexHour)}
                      defaultOption={hour.start}
                      width={50}
                    />
                    <Select
                      options={getHoursAndMinutes()}
                      inline
                      placeholder={'Hora fin'}
                      onChange={event => handleEndHour(event, indexDay, indexHour)}
                      defaultOption={hour.end}
                      width={40}
                    />
                    <Block marginLeft="base" marginTop="base">
                      <Icon
                        size="m"
                        content={crossIcon}
                        clickable
                        color="darkGrey"
                        onClick={() => removeHour(indexDay, indexHour)}
                      />
                    </Block>
                  </MediaQueryOwn>
                  <MediaQueryOwn maxWidth={theme.breakpoints.desktop - 1}>
                    <NativeSelect
                      marginRight="base"
                      options={getHoursAndMinutes()}
                      placeholder={'Hora inicio'}
                      onChange={event => handleStartHour(event, indexDay, indexHour)}
                      defaultValue={hour.start}
                      width={50}
                    />
                    <NativeSelect
                      options={getHoursAndMinutes()}
                      placeholder={'Hora fin'}
                      onChange={event => handleEndHour(event, indexDay, indexHour)}
                      defaultValue={hour.end}
                      width={40}
                    />
                    <Block marginLeft="base" marginTop="base">
                      <Icon
                        size="m"
                        content={crossIcon}
                        clickable
                        color="darkGrey"
                        onClick={() => removeHour(indexDay, indexHour)}
                      />
                    </Block>
                  </MediaQueryOwn>
                </Block>
                {indexHour === day.hours.length - 1 && (
                  <Text marginTop="base" menu>
                    <Link href="#" onClick={() => addHour(indexDay)}>
                      Añadir más horas este día
                    </Link>
                  </Text>
                )}
              </Block>
            </div>
          ))}
          {validateDay(day) && activeValidator && (
            <ErrorText error caption>
              {validateDay(day)}
            </ErrorText>
          )}
          <Division marginTop="mini" marginBottom="mini" />
        </div>
      ))}
      {!validateAllDays(days) && activeValidator && (
        <ErrorText error caption>
          Indica fecha y hora del servicio correctamente antes de continuar
        </ErrorText>
      )}
      {days.length === 0 && (
        <Text menu marginTop="small">
          Para reservar varios días, márcalos uno a uno en el calendario.
        </Text>
      )}
    </>
  )
}



// WEBPACK FOOTER //
// ./src/views/LongTermRecommendation/CheckoutForm/components/DatepickerField.tsx